import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import useFetchSubDepartments from "@/modules/common/hooks/subdepartments-data.hook";
import { SubDepartment } from "@/modules/common/types";
import { GetAgentTargetResponse } from "@/modules/targets/types";
import { muiTheme } from "@/styles/theme";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Typography,
} from "@mui/material";
import { PlusCircle } from "lucide-react";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/GridLegacy";

interface AgentsTargetsProps {
  viewOnly?: boolean;
  months: string[];
  onCreateAgentTarget: (department: {
    subDepartmentUId: string;
    subDepartmentName: string;
  }) => void;
  agentTargets: GetAgentTargetResponse | undefined;
  isLoadingAgentsTargets: boolean;
  isErrorFetchingAgentsTargets: boolean;
  onError?: () => void;
}

interface RankTarget {
  title: string;
  uid: string;
  m1: number;
  m2: number;
  m3: number;
  m1Min: number;
  m2Min: number;
  m3Min: number;
  total: number;
}

interface DepartmentWithRanks {
  name: string;
  uid: string;
  ranks: RankTarget[];
  total: number;
}

const AgentsTargets: React.FC<AgentsTargetsProps> = ({
  months,
  onCreateAgentTarget,
  agentTargets,
  isLoadingAgentsTargets,
  isErrorFetchingAgentsTargets,
  onError,
  viewOnly,
}) => {
  const { uid } = useParams();
  const { subDepartments: eligibleSubDepartmentsForTargets } = useFetchSubDepartments({
    organizationUId: uid,
    hasTarget: true,
  });

  const getQuarterDetails = (dateStr: Date) => {
    const date = new Date(dateStr);
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    const quarterStartMonth = Math.floor(month / 3) * 3;
    const quarterEndMonth = quarterStartMonth + 2;

    return {
      quarter,
      startMonth: quarterStartMonth,
      endMonth: quarterEndMonth,
      monthInQuarter: month - quarterStartMonth,
    };
  };

  const organizePeriodData = useMemo(() => {
    if (!agentTargets?.data?.length) return { departments: [] };

    const departmentMap = new Map<string, DepartmentWithRanks>();

    agentTargets.data.forEach((target) => {
      const deptName = target.SubDepartment.name;
      const quarterInfo = getQuarterDetails(target.from);
      const monthPosition = quarterInfo.monthInQuarter;

      if (!departmentMap.has(deptName)) {
        departmentMap.set(deptName, {
          name: deptName,
          uid: target.SubDepartment.uid,
          ranks: [],
          total: 0,
        });
      }

      const dept = departmentMap.get(deptName)!;
      let rank = dept.ranks.find((r) => r.uid === target.Rank.uid);

      if (!rank) {
        rank = {
          title: target.Rank.title,
          uid: target.Rank.uid,
          m1: 0,
          m2: 0,
          m3: 0,
          m1Min: 0,
          m2Min: 0,
          m3Min: 0,
          total: 0,
        };
        dept.ranks.push(rank);
      }

      // Correctly assign amount to the appropriate month
      switch (monthPosition) {
        case 0:
          rank.m1 = target.amount;
          rank.m1Min = target.minimumTarget || 0;
          break;
        case 1:
          rank.m2 = target.amount;
          rank.m2Min = target.minimumTarget || 0;
          break;
        case 2:
          rank.m3 = target.amount;
          rank.m3Min = target.minimumTarget || 0;
          break;
      }

      // Calculate total using display amounts (main amount or minimum amount if main is 0/null)
      const m1Display =
        rank.m1 === 0 || rank.m1 === null ? rank.m1Min : rank.m1;
      const m2Display =
        rank.m2 === 0 || rank.m2 === null ? rank.m2Min : rank.m2;
      const m3Display =
        rank.m3 === 0 || rank.m3 === null ? rank.m3Min : rank.m3;
      rank.total = m1Display + m2Display + m3Display;
      dept.total = dept.ranks.reduce((sum, r) => sum + r.total, 0);
    });

    return {
      departments: Array.from(departmentMap.values()),
    };
  }, [agentTargets?.data]);

  const isAnySubDepartmentHasMoreThanOneRank =
    organizePeriodData.departments.some((dept) => dept.ranks.length > 1);

  if (onError && isErrorFetchingAgentsTargets) {
    onError();
  }

  return (
    <Card
      sx={
        !viewOnly
          ? {
              bgcolor: muiTheme.colorSchemes.light.palette.main.white,
              boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
            }
          : {
              bgcolor: muiTheme.colorSchemes.light.palette.main.white,
              boxShadow: "none",
              borderRadius: 0,
              padding: 0,
            }
      }
    >
      <CardHeader
        title={
          <Box
            sx={{
              color: muiTheme.colorSchemes.light.palette.neutral[900],
              ...muiTheme.typography.h3_med,
            }}
          >
            Agents Targets
          </Box>
        }
      />
      <CardContent sx={{ overflow: "auto" }}>
        <Box sx={{ minWidth: "800px" }}>
          <Grid container spacing={1}>
            {/* Header row */}
            <Grid
              item
              mobile={isAnySubDepartmentHasMoreThanOneRank ? 3.6 : 2.4}
            >
              <Box
                sx={{
                  p: 1,
                  bgcolor: muiTheme.colorSchemes.light.palette.neutral[50],
                  borderRadius: 1,
                  height: 50,
                }}
              >
                <Box
                  sx={{
                    color: muiTheme.colorSchemes.light.palette.neutral[700],
                    ...muiTheme.typography.h4_med,
                  }}
                >
                  Sub department
                </Box>
              </Box>
            </Grid>
            {months.map((month) => (
              <Grid
                item
                mobile={isAnySubDepartmentHasMoreThanOneRank ? 2.1 : 2.4}
                key={month}
              >
                <Box
                  sx={{
                    borderRadius: 1,
                    p: 1,
                    pl: 2,
                    bgcolor: "#E6F0F4",
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: muiTheme.colorSchemes.light.palette.neutral[700],
                      ...muiTheme.typography.h4_med,
                    }}
                  >
                    {month}
                  </Typography>
                </Box>
              </Grid>
            ))}
            <Grid
              item
              mobile={isAnySubDepartmentHasMoreThanOneRank ? 2.1 : 2.4}
            >
              <Box
                sx={{
                  borderRadius: 1,
                  p: 1,
                  bgcolor: "#E6F0F4",
                  height: 50,
                }}
              >
                <Typography
                  sx={{
                    color: muiTheme.colorSchemes.light.palette.neutral[700],
                    ...muiTheme.typography.h4_med,
                  }}
                >
                  Q Total
                </Typography>
              </Box>
            </Grid>

            {/* Loading Skeleton */}
            {isLoadingAgentsTargets ? (
              <Grid item mobile={12}>
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
              </Grid>
            ) : (
              // Department rows
              eligibleSubDepartmentsForTargets.map((dept: SubDepartment) => {
                const departmentData = organizePeriodData.departments.find(
                  (d) => d.uid === dept.uid
                );
                const hasExistingTargets = Boolean(departmentData);

                return (
                  <React.Fragment key={dept.uid}>
                    <Grid item container spacing={1}>
                      <Grid
                        item
                        mobile={
                          isAnySubDepartmentHasMoreThanOneRank ? 1.8 : 2.4
                        }
                      >
                        <Box
                          sx={{
                            borderRadius: 1,
                            p: 1,
                            bgcolor:
                              muiTheme.colorSchemes.light.palette.neutral[50],
                            height:
                              departmentData?.ranks.length &&
                              departmentData?.ranks.length > 1
                                ? 110
                                : 50,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                muiTheme.colorSchemes.light.palette
                                  .neutral[700],
                              ...muiTheme.typography.h4_med,
                              textTransform: "capitalize",
                            }}
                          >
                            {dept.name}
                          </Typography>
                        </Box>
                      </Grid>
                      {!hasExistingTargets ? (
                        <Grid
                          item
                          mobile={
                            isAnySubDepartmentHasMoreThanOneRank ? 10.2 : 9.6
                          }
                        >
                          {!viewOnly ? (
                            <Button
                              variant="outlined"
                              fullWidth
                              startIcon={
                                <PlusCircle
                                  style={{
                                    color:
                                      muiTheme.colorSchemes.light.palette
                                        .primary[500],
                                  }}
                                />
                              }
                              sx={{
                                color:
                                  muiTheme.colorSchemes.light.palette
                                    .primary[500],
                                borderColor:
                                  muiTheme.colorSchemes.light.palette
                                    .neutral[100],
                                bgcolor: "#F9F9F9",
                                minHeight: 50,
                                textTransform: "none",
                                "&:hover": {
                                  borderColor:
                                    muiTheme.colorSchemes.light.palette
                                      .primary[500],
                                  bgcolor:
                                    muiTheme.colorSchemes.light.palette
                                      .neutral[100],
                                },
                                "&:active": {
                                  bgcolor:
                                    muiTheme.colorSchemes.light.palette
                                      .neutral[50],
                                },
                              }}
                              onClick={() =>
                                onCreateAgentTarget({
                                  subDepartmentUId: dept.uid,
                                  subDepartmentName: dept.name,
                                })
                              }
                            >
                              Create
                            </Button>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                backgroundColor: "#F9F9F9",
                              }}
                            >
                              <Typography
                                sx={{
                                  color:
                                    muiTheme.colorSchemes.light.palette
                                      .neutral[700],
                                  ...muiTheme.typography.h4_med,
                                }}
                              >
                                No targets found for this quarter
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      ) : (
                        <Grid
                          item
                          container
                          mobile={
                            isAnySubDepartmentHasMoreThanOneRank ? 10.2 : 9.6
                          }
                        >
                          {departmentData?.ranks.map((rank) => (
                            <Grid
                              container
                              spacing={1}
                              key={rank.uid}
                              sx={{
                                mb: 1,
                                alignItems: "center",
                              }}
                            >
                              {isAnySubDepartmentHasMoreThanOneRank && (
                                <Grid item mobile={2.1}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      pl: 2,
                                      bgcolor:
                                        muiTheme.colorSchemes.light.palette
                                          .neutral[50],
                                      borderRadius: 1,
                                      height: 50,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color:
                                          muiTheme.colorSchemes.light.palette
                                            .neutral[700],
                                        ...muiTheme.typography.h4_med,
                                      }}
                                    >
                                      {rank.title}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}

                              {[
                                { amount: rank.m1, minAmount: rank.m1Min },
                                { amount: rank.m2, minAmount: rank.m2Min },
                                { amount: rank.m3, minAmount: rank.m3Min },
                              ].map((monthData, index) => {
                                const displayAmount =
                                  monthData.amount === 0 ||
                                  monthData.amount === null
                                    ? monthData.minAmount
                                    : monthData.amount;

                                return (
                                  <Grid
                                    item
                                    key={index}
                                    mobile={
                                      isAnySubDepartmentHasMoreThanOneRank
                                        ? 2.475
                                        : 3
                                    }
                                  >
                                    <Box
                                      sx={{
                                        p: 1,
                                        pl: 2,
                                        height: 50,
                                        display: "flex",
                                        alignItems: "center",
                                        bgcolor: "#E6F0F4",
                                        borderRadius: 1,
                                      }}
                                    >
                                      {displayAmount.toLocaleString()} EGP
                                    </Box>
                                  </Grid>
                                );
                              })}
                              <Grid
                                item
                                mobile={
                                  isAnySubDepartmentHasMoreThanOneRank
                                    ? 2.475
                                    : 3
                                }
                              >
                                <Box
                                  sx={{
                                    p: 1,
                                    pl: 2,
                                    height: 50,
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#E6F0F4",
                                    borderRadius: 1,
                                  }}
                                >
                                  {rank.total.toLocaleString()} EGP
                                </Box>
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Grid>
                  </React.Fragment>
                );
              })
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default WithErrorHandling(AgentsTargets);
